import { useState, useEffect } from 'react';

export default function AboutMe() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('about');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  return (
    <section id="about" className="relative py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className={`mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-3">
            <span className="inline-block text-xs font-semibold tracking-[0.25em] uppercase text-[#800000] mb-4 px-6 py-2 border border-[#800000]/20 bg-[#800000]/5">About</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-black text-center mb-4 tracking-tight" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}>
            Brayan Fernandez
          </h2>
          <p className="text-base text-center text-gray-600 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Shaped by movement, sacrifice, and an unshakable sense of responsibility
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Column - Image */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="relative group">
              <div className="relative w-full h-[600px] overflow-hidden bg-black">
                <img
                  src="https://static.readdy.ai/image/165557c1c60078d8d9753efdcb1d1569/7a42b5e8f6443fa30ce2fc29303beb29.jpeg"
                  alt="Brayan Fernandez Professional Portrait"
                  className="w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 border border-black/10"></div>
              </div>
              
              {/* Stats Below Image */}
              <div className="grid grid-cols-3 gap-px bg-gray-200 mt-8">
                <div className="text-center bg-white p-6 group cursor-pointer hover:bg-gray-50 transition-colors duration-300">
                  <div className="text-3xl font-bold text-[#800000] mb-1 group-hover:scale-110 transition-transform duration-300" style={{ fontFamily: "'Inter', sans-serif" }}>18</div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>Years Old</div>
                </div>
                <div className="text-center bg-white p-6 group cursor-pointer hover:bg-gray-50 transition-colors duration-300">
                  <div className="text-3xl font-bold text-[#800000] mb-1 group-hover:scale-110 transition-transform duration-300" style={{ fontFamily: "'Inter', sans-serif" }}>3</div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>Projects</div>
                </div>
                <div className="text-center bg-white p-6 group cursor-pointer hover:bg-gray-50 transition-colors duration-300">
                  <div className="text-3xl font-bold text-[#800000] mb-1 group-hover:scale-110 transition-transform duration-300" style={{ fontFamily: "'Inter', sans-serif" }}>20+</div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>Awards</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="space-y-10">
              {/* Story Section 1 */}
              <div className="group">
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#800000] text-white font-bold text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                    01
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-black mb-3 tracking-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Origins & Journey
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", lineHeight: '1.8' }}>
                      I am <strong className="text-black font-semibold">Brayan Fernandez</strong>, shaped by movement, sacrifice, and an unshakable sense of responsibility. I was born in <span className="text-[#800000] font-medium">Cuba</span> and arrived in the United States carrying more hope than certainty, learning quickly that resilience is built in quiet moments, when no one is watching, and quitting would be easier. Opportunity, for me, has never been assumed; it has been earned through <span className="text-[#800000] font-medium">discipline, service, and faith</span> in what is possible.
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gray-200"></div>

              {/* Story Section 2 */}
              <div className="group">
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#800000] text-white font-bold text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                    02
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-black mb-3 tracking-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Leadership & Service
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", lineHeight: '1.8' }}>
                      I lead not for recognition, but to repair what is missing. In my community, I build systems of care, <span className="text-[#800000] font-medium">feeding families, mentoring students</span>, and creating spaces where dignity is restored long before help is asked for. My commitment to medicine, particularly <strong className="text-black font-semibold">neuroscience</strong>, is rooted in listening: to pain, to silence, to stories that demand presence rather than speed.
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gray-200"></div>

              {/* Story Section 3 */}
              <div className="group">
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#800000] text-white font-bold text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                    03
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-black mb-3 tracking-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Philosophy & Purpose
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", lineHeight: '1.8' }}>
                      When words fall short, I speak through <span className="text-[#800000] font-medium">classical guitar</span>; when doubt appears, I train my body to remember strength. Every path I walk—academic, artistic, or service-driven—points toward the same truth: <strong className="text-black font-semibold">leadership is not about being seen, but about making others feel seen.</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Quote Section */}
              <div className="mt-12 relative bg-gray-50 p-8 border-l-4 border-[#800000]">
                <blockquote className="relative">
                  <p className="text-lg font-light text-gray-800 italic leading-relaxed mb-3" style={{ fontFamily: "'Georgia', serif", lineHeight: '1.8' }}>
                    The only way to build something that lasts is to serve with the same heart you started with.
                  </p>
                  <cite className="text-xs font-semibold uppercase tracking-wider text-[#800000] not-italic" style={{ fontFamily: "'Inter', sans-serif" }}>— Brayan Fernandez</cite>
                </blockquote>
              </div>

              {/* CTA Button */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <a
                  href="#awards"
                  className="group inline-flex items-center gap-3 bg-[#800000] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-black transition-all duration-300 whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <span>Explore My Journey</span>
                  <i className="ri-arrow-right-line text-lg group-hover:translate-x-2 transition-transform duration-300 w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
